import React from 'react';
import { Link } from '@tanstack/react-router';
import { FiPlayCircle } from 'react-icons/fi';
import { cn } from '../../utils/cn';
import { useTour } from '../../onboarding/useTour';

type EmptyStateAction =
  | { label: string; onClick: () => void; to?: never; icon?: React.ReactNode }
  | { label: string; to: string; onClick?: never; icon?: React.ReactNode };

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  /** One line on what will appear here and how to get started. */
  description?: React.ReactNode;
  action?: EmptyStateAction;
  /** Adds a "Show me how" link that starts this tour. */
  tourId?: string;
  /** `compact` suits panels and lists; `default` suits whole pages and tabs. */
  size?: 'default' | 'compact';
  className?: string;
}

const actionClass =
  'inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  tourId,
  size = 'default',
  className,
}) => {
  const { start, tours } = useTour();
  const canShowTour = !!tourId && tours.some((t) => t.id === tourId);
  const compact = size === 'compact';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-4 py-10' : 'px-4 py-16 sm:py-20',
        className,
      )}
    >
      {icon && (
        <div
          className={cn(
            'mb-4 flex items-center justify-center rounded-2xl border border-white/5 bg-zinc-900 text-zinc-400',
            compact ? 'h-12 w-12' : 'h-16 w-16',
          )}
          aria-hidden
        >
          {icon}
        </div>
      )}
      <h3 className={cn('font-semibold text-white', compact ? 'text-[15px]' : 'text-lg')}>
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            'mx-auto mt-1 max-w-sm leading-relaxed text-zinc-500',
            compact ? 'text-[13px]' : 'text-sm',
          )}
        >
          {description}
        </p>
      )}
      {(action || canShowTour) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {action &&
            (action.to ? (
              <Link to={action.to} className={actionClass}>
                {action.icon}
                {action.label}
              </Link>
            ) : (
              <button type="button" onClick={action.onClick} className={actionClass}>
                {action.icon}
                {action.label}
              </button>
            ))}
          {canShowTour && (
            <button
              type="button"
              onClick={() => start(tourId)}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-zinc-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            >
              <FiPlayCircle size={15} aria-hidden />
              Show me how
            </button>
          )}
        </div>
      )}
    </div>
  );
};
